"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { LuTag } from "react-icons/lu";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function ReviewsList({ client }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!client?.id) {
        setLoading(false);
        return;
      }

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error("Authorization token not found");
        setLoading(false);
        return;
      }

      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(
          `${BASE_URL}/client/client-view/${client.id}/reviews/`,
          {
            method: "GET",
            headers,
          }
        );

        if (!res.ok) {
          toast.error(`Error ${res.status}: failed to fetch reviews`);
          setLoading(false);
          return;
        }

        const data = await res.json();
        setReviews(data || []);
      } catch (err) {
        console.error("❌ Fetch failed:", err);
        toast.error("Network error — check console");
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [client?.id]);

  return (
    <div className="w-full mt-10">
      <h3 className="font-normal text-[#302B2B] text-[25px] leading-[30px]">
        Reviews
      </h3>

      {loading ? (
        <div className="flex justify-center py-20 mt-10">
          <div className="w-10 h-10 border-4 border-[#5563F6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : reviews.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-gray-500 text-center mt-10"
        >
          No reviews yet.
        </motion.div>
      ) : (
        <Swiper
          slidesPerView={2.6}
          grabCursor={true}
          className="pb-6"
          breakpoints={{
            0: { slidesPerView: 1.1, spaceBetween: 20 },
            768: { slidesPerView: 1.8, spaceBetween: 24 },
            1024: { slidesPerView: 2.4, spaceBetween: 30 },
          }}
        >
          {reviews.map((r, index) => (
            <SwiperSlide key={r.id || index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.15,
                  ease: "easeOut",
                }}
                viewport={{ once: true }}
                className="flex flex-col items-start p-[26px_20px] gap-[20px] rounded-[20px] w-[553px] h-[280px] shadow-sm"
              >
                <div className="flex justify-between items-center w-full">
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        client?.image_url ||
                        "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                      }
                      alt="review avatar"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex flex-col leading-tight">
                      <p className="font-semibold text-[#302B2B] text-[16px]">
                        {client?.name || r.client_name || "Client"}
                      </p>
                      <p className="text-gray-500 text-[14px]">
                        {r.service_name || "Service"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center  rounded-[10px] px-3 py-2 gap-[8px] text-[#302B2B]">
                    <LuTag className="w-[16px] h-[16px] text-[#302B2B]" />
                    <span className="text-[14px] font-medium">
                      Service price: {r.service_price || 0}
                    </span>
                  </div>
                </div>

                <div className="flex gap-[4px]">
                  {[...Array(5)].map((_, i) => {
                    const rating = parseFloat(r.rating) || 0;
                    if (rating >= i + 1)
                      return (
                        <FaStar key={i} className="text-[#030735] w-5 h-5" />
                      );
                    if (rating >= i + 0.5)
                      return (
                        <FaStarHalfAlt
                          key={i}
                          className="text-[#030735] w-5 h-5"
                        />
                      );
                    return (
                      <FaRegStar key={i} className="text-gray-300 w-5 h-5" />
                    );
                  })}
                </div>

                <p className="text-gray-600 text-[14px] leading-relaxed">
                  {r.comment || "No comment provided."}
                </p>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}